import { Box, Card, Typography, useMediaQuery, useTheme } from '@mui/material';
import { BarChart } from '@mui/x-charts';
import { JSX, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    barValueLabel,
    compactMoney,
    formatMoney,
    formatTrendMonth,
    INCOME_MODE_LABELS,
} from '../../../library/dashboardDisplay';
import { ITherapistOwnTrendPoint } from '../../../library/models/therapistDashboard';
import { useSeriesVisibility } from '../../../library/seriesVisibility';
import ResourcesIds from '../../../resources/ResourcesIds';
import { therapistSeriesColor, TOTAL_SERIES_COLOR } from '../../../theme/chartPalette';
import { AboveBarLabel } from '../../dashboard/AboveBarLabel';
import { ChartLegend, ChartLegendItem } from '../../dashboard/ChartLegend';
import { ChartScroller } from '../../dashboard/ChartScroller';
import {
    chartCardStyles,
    chartEmptyBoxStyles,
    chartSubtitleStyles,
    chartTitleStyles,
} from '../../dashboard/monthlyChart.styles';
import {
    axisMaxWithHeadroom,
    BAR_BORDER_RADIUS,
    BAR_GAP_RATIO,
    CATEGORY_GAP_RATIO,
    chartHeightFor,
    labelFontSizeFor,
    monthTickInterval,
    tickLabelStyleFor,
} from '../../dashboard/monthlyChartConfig';

const BILLED_SERIES_ID = 'BILLED';
const EARNED_SERIES_ID = 'EARNED';

interface TherapistIncomeChartProps {
    trend: ITherapistOwnTrendPoint[];
}

/**
 * One therapist's year: what they invoiced and what they delivered, month against month.
 *
 * Two series rather than the clinic chart's Total-plus-colleagues, and they are the *two
 * readings of the same money* rather than two quantities -- so the pair of bars in each
 * month is exactly the comparison the page is for. A tall billed bar in June beside a
 * short earned one is a package sold and not yet worked off; the reverse, months later, is
 * that package being worked off.
 *
 * Billed takes the palette's first slot, the one the clinic chart gives its Total, because
 * this is likewise the figure a reader anchors on. Earned takes the next, so the two are
 * distinguishable without either reading as subordinate.
 */
export const TherapistIncomeChart = ({ trend }: TherapistIncomeChartProps): JSX.Element => {
    const theme = useTheme();
    const { t: translate, i18n } = useTranslation();
    const isNarrow = useMediaQuery(theme.breakpoints.down('sm'));

    const months: string[] = trend.map(point => formatTrendMonth(point.month, i18n));

    const allSeries = useMemo(
        () => [
            {
                id: BILLED_SERIES_ID,
                label: translate(INCOME_MODE_LABELS.BILLED),
                color: TOTAL_SERIES_COLOR,
                data: trend.map(point => point.billed),
            },
            {
                id: EARNED_SERIES_ID,
                label: translate(INCOME_MODE_LABELS.EARNED),
                color: therapistSeriesColor(0),
                data: trend.map(point => point.earned),
            },
        ],
        [trend, translate]
    );

    const seriesIds: string[] = useMemo(() => allSeries.map(series => series.id), [allSeries]);
    const { hidden, toggle } = useSeriesVisibility(seriesIds);

    const visible = allSeries.filter(series => !hidden.has(series.id));
    const legendItems: ChartLegendItem[] = allSeries.map(({ id, label, color }) => ({ id, label, color }));

    const hasIncome: boolean = allSeries.some(series => series.data.some(value => value > 0));

    return (
        <Card sx={chartCardStyles}>
            <Typography variant="h6" sx={chartTitleStyles}>
                {translate(ResourcesIds.THERAPIST_INCOME_CHART_TITLE)}
            </Typography>

            <Typography variant="caption" sx={chartSubtitleStyles}>
                {translate(ResourcesIds.THERAPIST_INCOME_CHART_SUBTITLE)}
            </Typography>

            {!hasIncome ? (
                <Box sx={chartEmptyBoxStyles}>
                    <Typography variant="body2">
                        {translate(ResourcesIds.DASHBOARD_CHART_EMPTY)}
                    </Typography>
                </Box>
            ) : (
                <>
                    <ChartLegend
                        items={legendItems}
                        hidden={hidden}
                        onToggle={toggle}
                        groupLabel={translate(ResourcesIds.DASHBOARD_LEGEND_LABEL)}
                    />

                    <ChartScroller
                        visibleSeries={visible.length}
                        label={translate(ResourcesIds.THERAPIST_INCOME_CHART_TITLE)}
                    >
                        <BarChart
                            height={chartHeightFor(isNarrow)}
                            xAxis={[
                                {
                                    scaleType: 'band',
                                    data: months,
                                    tickLabelInterval: monthTickInterval,
                                    tickLabelStyle: tickLabelStyleFor(isNarrow),
                                    categoryGapRatio: CATEGORY_GAP_RATIO,
                                    barGapRatio: BAR_GAP_RATIO,
                                },
                            ]}
                            // Headroom from the visible series only, so hiding the taller
                            // of the two lets the other use the height.
                            yAxis={[
                                {
                                    max: axisMaxWithHeadroom(visible.flatMap(series => series.data)),
                                },
                            ]}
                            series={visible.map(series => ({
                                ...series,
                                valueFormatter: (value: number | null): string =>
                                    formatMoney(value ?? 0, translate),
                            }))}
                            // Both are required: the function prints the zeros the built-in
                            // shortcut suppresses, and the slot is what puts the text above
                            // the bar instead of inside it.
                            barLabel={barValueLabel(value => compactMoney(value, i18n))}
                            slots={{ barLabel: AboveBarLabel }}
                            slotProps={{ barLabel: { labelFontSize: labelFontSizeFor(isNarrow) } as never }}
                            borderRadius={BAR_BORDER_RADIUS}
                            hideLegend
                        />
                    </ChartScroller>
                </>
            )}
        </Card>
    );
};
